import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  ChevronDown,
  Clock3,
  MessageCircle,
  Minus,
  Plus,
  ShoppingBag,
  Star,
} from "lucide-react";
import { Button } from "../components/ui/button";
import SEOHead from "../components/SEOHead";
import KuponConversionTemplate from "../components/lp-templates/kupon-conversion";
import MediaPreviewModal from "../components/MediaPreviewModal";

import PublicBreadcrumbs from "../components/PublicBreadcrumbs";
import { useApp } from "../context/AppContext";
import { formatRupiah } from "../lib/format";
import { landingPagesApi } from "../lib/api";
import { CONTACT_CONFIG, DEFAULT_OG_IMAGE } from "../lib/site";
import { buildBreadcrumbSchema, buildFaqSchema, buildProductSchema } from "../lib/seo";
import { initMetaPixel, trackEvent, getGlobalPixelId } from "../lib/metaPixel";

// Render text that may contain newlines as proper line breaks
const TextBlock = ({ text, className = "" }) => {
  if (!text) return null;
  if (!text.includes("\n")) return <p className={className}>{text}</p>;
  return (
    <p className={className}>
      {text.split("\n").map((line, i, arr) => (
        <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
      ))}
    </p>
  );
};

const SingleProductLandingPage = () => {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const { products, addToCart } = useApp();
  const [landingPage, setLandingPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [timeLeft, setTimeLeft] = useState("00:00:00");
  const [showSticky, setShowSticky] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    landingPagesApi
      .getBySlug(slug)
      .then((data) => { if (mounted) setLandingPage(data); })
      .catch(() => { if (mounted) setLandingPage(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [slug]);

  // Build sections from the saved page data
  const sections = useMemo(() => {
    if (!landingPage) return [];
    return (landingPage.sections || [])
      .filter((s) => s.is_enabled !== false)
      .sort((a, b) => (a.section_order || 0) - (b.section_order || 0))
      .map((s) => ({
        id: s.id,
        type: s.section_type,
        order: s.section_order,
        content: s.config_json?.content || s.config_json || {},
        style: s.config_json?.style || {},
        image_source: s.image_source || "product",
        image_url: s.image_url || "",
      }));
  }, [landingPage]);

  const product = useMemo(() => {
    if (landingPage?.product) return landingPage.product;
    return products.find((p) => p.slug === slug || p.name?.toLowerCase().replace(/\s+/g, "-") === slug);
  }, [landingPage, products, slug]);

  const pagePath = `/lp/${slug}`;
  const displayName = product?.name || landingPage?.title || "Produk";
  const productImage = landingPage?.custom_image || product?.image || DEFAULT_OG_IMAGE;
  const price = Number(product?.price || 0);
  const comparePrice = Number(product?.compare_price || 0);
  const hasComparePrice = comparePrice > price && price > 0;
  const discountPercent = hasComparePrice ? Math.round(((comparePrice - price) / comparePrice) * 100) : 0;
  const stock = Number(product?.stock || 0);
  const ctaConfig = landingPage?.cta_config || {};
  const ctaMode = ctaConfig.mode || "both";
  const checkoutEnabled = ctaMode !== "whatsapp" && ctaConfig.checkout_enabled !== false;
  const whatsappEnabled = ctaMode !== "web" && ctaConfig.whatsapp_enabled !== false;
  const productAvailable = Boolean(product) && product.is_active !== false && stock > 0;
  const canCheckout = checkoutEnabled && productAvailable;
  const total = price * qty;
  const whatsappText = ctaConfig.whatsapp_text || `Halo, saya ingin bertanya tentang ${displayName}.`;
  const whatsappUrl = `${CONTACT_CONFIG.whatsappUrl}?text=${encodeURIComponent(whatsappText)}`;

  const faqSection = sections.find((s) => s.type === "faq");
  const breadcrumbItems = [
    { name: "Beranda", path: "/" },
    { name: displayName, path: pagePath },
  ];
  const schema = [
    buildBreadcrumbSchema(breadcrumbItems),
    faqSection && buildFaqSchema(faqSection.content?.items || []),
    product && buildProductSchema(product, { path: pagePath, description: landingPage?.description, image: productImage }),
  ].filter(Boolean);

  // Initialize Meta Pixel (per-LP or global)
  useEffect(() => {
    const pixelId = landingPage?.cta_config?.meta_pixel_id || getGlobalPixelId();
    if (pixelId) {
      initMetaPixel(pixelId);
      trackEvent("ViewContent", {
        content_name: displayName,
        content_type: "product",
        content_ids: product?.id ? [String(product.id)] : [],
        value: price,
        currency: "IDR",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landingPage]);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const target = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      const diff = Math.max(target.getTime() - now.getTime(), 0);
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const mins = Math.floor((diff / 1000 / 60) % 60);
      const secs = Math.floor((diff / 1000) % 60);
      setTimeLeft(`${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Sticky CTA
  useEffect(() => {
    const handleScroll = () => setShowSticky(window.scrollY > 420);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const changeQty = (nextQty) => {
    const val = Math.max(1, Number(nextQty) || 1);
    setQty(stock > 0 ? Math.min(val, stock) : val);
  };

  const checkoutNow = () => {
    if (!canCheckout) {
      toast.error("Produk belum tersedia untuk checkout");
      return;
    }
    addToCart(product.id, qty);
    trackEvent("AddToCart", {
      content_name: displayName,
      content_ids: [String(product.id)],
      content_type: "product",
      value: total,
      currency: "IDR",
    });
    toast.success(`${qty} ${displayName} ditambahkan ke checkout`);
    // Small delay to ensure pixel event is sent before navigation
    setTimeout(() => navigate("/checkout/pengiriman"), 300);
  };

  const WhatsappButton = ({ label = "Tanya via WhatsApp", className = "" }) =>
    whatsappEnabled ? (
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noreferrer"
        className={className}
        onClick={() => trackEvent("Lead", { content_name: displayName, content_category: "WhatsApp" })}
      >
        <MessageCircle className="mr-2 h-5 w-5" />
        {label}
      </a>
    ) : null;

  // Get image for a section
  const getSectionImage = (section) => {
    if (section.image_source === "url" && section.image_url) return section.image_url;
    return productImage;
  };

  // ─── Section Renderers ─────────────────────────────────────────────────────
  const renderSection = (section) => {
    const { content, style } = section;
    const accent = style?.accent || "#0284c7";
    const bg = style?.bg || "#ffffff";

    // ── HERO ──
    if (section.type === "hero") {
      const image = getSectionImage(section);
      return (
        <section key={section.id} className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-sky-50/30 to-cyan-50/40">
          <div className="mx-auto grid min-h-[80vh] max-w-7xl items-center gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:py-16">
            <div>
              <PublicBreadcrumbs items={breadcrumbItems} />
              {content.title && (
                <h1 className="mt-6 font-display text-4xl font-semibold leading-tight tracking-tight text-stone-950 sm:text-5xl lg:text-6xl">
                  {content.title}
                </h1>
              )}
              {content.desc && (
                <TextBlock text={content.desc} className="mt-5 max-w-2xl text-lg leading-8 text-stone-600" />
              )}
              {content.bullets && Array.isArray(content.bullets) && content.bullets.length > 0 && (
                <div className="mt-7 grid gap-3 sm:grid-cols-2">
                  {content.bullets.map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm font-medium text-stone-800">
                      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                        <CheckCircle2 className="h-4 w-4" />
                      </span>
                      {item}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                {checkoutEnabled && (
                  <Button type="button" onClick={checkoutNow} disabled={!canCheckout} className="h-14 rounded-xl bg-sky-600 px-6 text-base font-semibold text-white shadow-lg shadow-sky-200 hover:bg-sky-700">
                    <ShoppingBag className="mr-2 h-5 w-5" /> {content.cta_text || "Checkout Sekarang"}
                  </Button>
                )}
                <WhatsappButton label="Tanya via WhatsApp" className="inline-flex h-14 items-center justify-center rounded-xl border border-sky-200 bg-white px-6 text-base font-semibold text-sky-700 transition hover:bg-sky-50" />
              </div>
            </div>
            <div className="overflow-hidden rounded-[2rem] border border-white bg-white shadow-2xl shadow-sky-100">
              <div className="aspect-[4/3] bg-slate-100">
                <img src={image} alt={displayName} className="h-full w-full object-cover" />
              </div>
              <div className="space-y-5 p-5 sm:p-6">
                <div className="flex items-start justify-between gap-4">
                  <h2 className="font-display text-2xl font-semibold text-stone-950">{displayName}</h2>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${productAvailable ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                    {productAvailable ? `Stok ${stock}` : "Belum tersedia"}
                  </span>
                </div>
                <div>
                  {hasComparePrice && (
                    <div className="space-y-1">
                      <p className="text-lg text-stone-400 line-through">{formatRupiah(comparePrice)}</p>
                      <p className="text-sm font-semibold text-rose-600">Diskon {discountPercent}%</p>
                    </div>
                  )}
                  <p className="font-display text-3xl font-semibold text-sky-700">{price ? formatRupiah(price) : "Harga mengikuti katalog"}</p>
                </div>
                <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <span className="text-sm font-medium text-stone-700">Jumlah</span>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => changeQty(qty - 1)} className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-stone-700 hover:bg-slate-100"><Minus className="h-4 w-4" /></button>
                    <input value={qty} onChange={(e) => changeQty(e.target.value)} inputMode="numeric" className="h-10 w-14 rounded-xl border border-slate-200 bg-white text-center font-semibold outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100" />
                    <button type="button" onClick={() => changeQty(qty + 1)} className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-stone-700 hover:bg-slate-100"><Plus className="h-4 w-4" /></button>
                  </div>
                </div>
                {checkoutEnabled && (
                  <Button type="button" onClick={checkoutNow} disabled={!canCheckout} className="h-14 w-full rounded-xl bg-sky-600 text-base font-semibold text-white hover:bg-sky-700">
                    Checkout {price ? formatRupiah(total) : "Sekarang"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>
      );
    }

    // ── PROBLEM ──
    if (section.type === "problem") {
      const items = content.items || [];
      return (
        <section key={section.id} className="bg-white py-16 sm:py-20" style={{ backgroundColor: bg }}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {content.title && <h2 className="text-center font-display text-3xl font-semibold text-stone-950 sm:text-4xl">{content.title}</h2>}
            {content.desc && <p className="mx-auto mt-3 max-w-3xl text-center text-base leading-7 text-stone-600 whitespace-pre-wrap">{content.desc}</p>}
            <div className="mt-12 grid gap-5 sm:grid-cols-2 md:grid-cols-3">
              {items.map((item, i) => (
                <article key={i} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    {item.icon && <div className="text-3xl shrink-0">{item.icon}</div>}
                    <div>
                      <div className="text-rose-500 font-bold text-xs uppercase tracking-wider">✕ Masalah</div>
                      <h3 className="mt-1 text-lg font-semibold text-stone-950">{item.title}</h3>
                      {item.desc && <p className="mt-2 leading-7 text-stone-600">{item.desc}</p>}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      );
    }

    // ── BENEFIT ──
    if (section.type === "benefit") {
      const items = content.items || [];
      return (
        <section key={section.id} className="py-16 sm:py-20" style={{ backgroundColor: bg }}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {content.title && <h2 className="text-center font-display text-3xl font-semibold text-stone-950 sm:text-4xl">{content.title}</h2>}
            {content.desc && <p className="mx-auto mt-3 max-w-3xl text-center text-base leading-7 text-stone-600 whitespace-pre-wrap">{content.desc}</p>}
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {items.map((item, i) => {
                const isObj = typeof item === "object" && item !== null;
                const text = isObj ? (item.text || item.title || "") : item;
                const icon = isObj ? item.icon : null;
                const desc = isObj ? item.desc : null;
                return (
                  <div key={i} className="rounded-2xl border border-white/15 bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
                    {icon ? (
                      <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-600 text-white grid place-items-center text-2xl shadow-lg">{icon}</div>
                    ) : (
                      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white text-xs font-bold" style={{ background: accent }}>✓</span>
                    )}
                    <h3 className="mt-4 font-bold text-lg text-stone-950">{text}</h3>
                    {desc && <p className="mt-2 text-sm leading-6 text-stone-600">{desc}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      );
    }

    // ── INGREDIENTS ──
    if (section.type === "ingredients") {
      const image = getSectionImage(section);
      return (
        <section key={section.id} className="py-16 sm:py-20" style={{ backgroundColor: bg }}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-2 items-center">
              <div>
                {content.title && <h2 className="font-display text-3xl font-semibold text-stone-950 sm:text-4xl">{content.title}</h2>}
                {content.desc && <TextBlock text={content.desc} className="mt-4 text-base leading-7 text-stone-600" />}
              </div>
              {image && (
                <div className="aspect-square overflow-hidden rounded-2xl">
                  <img src={image} alt={content.title || "Ingredients"} className="h-full w-full object-cover" />
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    // ── STATS ──
    if (section.type === "stats") {
      const items = content.items || [];
      return (
        <section key={section.id} className="bg-gradient-to-r from-sky-50 to-cyan-50 py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {content.title && <h2 className="text-center font-display text-3xl font-semibold text-stone-950 sm:text-4xl">{content.title}</h2>}
            {content.desc && <p className="mx-auto mt-3 max-w-3xl text-center text-base leading-7 text-stone-600 whitespace-pre-wrap">{content.desc}</p>}
            <div className={`${content.title || content.desc ? "mt-10" : ""} grid gap-4 md:grid-cols-4`}>
              {items.map((item, i) => (
                <div key={i} className="rounded-2xl border border-white bg-white/80 p-6 text-center shadow-sm">
                  <p className="font-display text-3xl font-semibold" style={{ color: accent }}>{item.number}</p>
                  <p className="mt-2 text-sm leading-6 text-stone-600">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    }

    // ── HOW TO USE / STEPS ──
    if (section.type === "how_to_use") {
      const items = content.items || [];
      return (
        <section key={section.id} className="bg-white py-16 sm:py-20" style={{ backgroundColor: bg }}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {content.title && <h2 className="text-center font-display text-3xl font-semibold text-stone-950 sm:text-4xl">{content.title}</h2>}
            {content.desc && <p className="mx-auto mt-3 max-w-3xl text-center text-base leading-7 text-stone-600 whitespace-pre-wrap">{content.desc}</p>}
            <div className="mt-12 grid gap-5 md:grid-cols-4">
              {items.map((item, i) => (
                <article key={i} className="relative rounded-2xl border border-sky-100 bg-sky-50/60 p-6">
                  <span className="absolute -top-4 left-5 inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: accent }}>{i + 1}</span>
                  <h3 className="mt-3 text-lg font-semibold text-stone-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-stone-600">{item.desc}</p>
                </article>
              ))}
            </div>
            {content.tip && (
              <div className="mt-8 mx-auto max-w-2xl rounded-2xl bg-sky-50 border border-sky-200 p-5 text-center text-sm text-stone-700">
                💡 <strong>Tips:</strong> {content.tip}
              </div>
            )}
          </div>
        </section>
      );
    }

    // ── TESTIMONIAL ──
    if (section.type === "testimonial") {
      const items = content.items || [];
      return (
        <section key={section.id} className="bg-gradient-to-r from-sky-50 to-cyan-50 py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {content.title && <h2 className="text-center font-display text-3xl font-semibold text-stone-950 sm:text-4xl">{content.title}</h2>}
            {content.desc && <p className="mx-auto mt-3 max-w-3xl text-center text-base leading-7 text-stone-600 whitespace-pre-wrap">{content.desc}</p>}
            <div className="mt-12 grid gap-5 md:grid-cols-3">
              {items.map((item, i) => (
                <article key={i} className="rounded-2xl border border-white bg-white p-6 shadow-sm">
                  <div className="flex gap-1 text-amber-400">
                    {[1, 2, 3, 4, 5].map((star) => <Star key={star} className="h-4 w-4 fill-current" />)}
                  </div>
                  {item.image_url && (
                    <div className="mt-4 overflow-hidden rounded-2xl border border-sky-100 bg-sky-50">
                      <button
                        type="button"
                        onClick={() =>
                          setPreviewMedia({
                            url: item.image_url,
                            title: item.name ? `Lampiran testimoni ${item.name}` : "Lampiran testimoni",
                            type: "image",
                          })
                        }
                        className="block h-full w-full cursor-zoom-in"
                        aria-label={item.name ? `Perbesar lampiran testimoni ${item.name}` : "Perbesar lampiran testimoni"}
                      >
                        <img
                          src={item.image_url}
                          alt={item.name ? `Lampiran testimoni ${item.name}` : "Lampiran testimoni"}
                          className="h-52 w-full object-cover transition duration-300 hover:scale-[1.02]"
                        />
                      </button>
                    </div>
                  )}
                  <p className="mt-4 leading-7 text-stone-700">"{item.text}"</p>
                  <div className="mt-5">
                    <p className="font-semibold text-stone-950">{item.name}</p>
                    {item.role && <p className="text-sm text-stone-500">{item.role}</p>}
                    {item.image_url && <p className="mt-1 text-xs font-medium uppercase tracking-wide text-sky-600">Lampiran testimoni</p>}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      );
    }

    // ── FAQ ──
    if (section.type === "faq") {
      const items = content.items || [];
      return (
        <section key={section.id} className="bg-white py-16 sm:py-20" style={{ backgroundColor: bg }}>
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            {content.title && <h2 className="text-center font-display text-3xl font-semibold text-stone-950 sm:text-4xl">{content.title}</h2>}
            {content.desc && <p className="mx-auto mt-3 max-w-3xl text-center text-base leading-7 text-stone-600 whitespace-pre-wrap">{content.desc}</p>}
            <div className="mt-10 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
              {items.map((item, i) => (
                <details key={i} className="group p-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-stone-950">
                    {item.q}
                    <ChevronDown className="h-5 w-5 text-stone-400 transition group-open:rotate-180" />
                  </summary>
                  <p className="mt-3 leading-7 text-stone-600">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      );
    }

    // ── ABOUT PRODUCT (Mengenal) ──
    if (section.type === "about_product") {
      return (
        <section key={section.id} className="py-16 text-white sm:py-20" style={{ background: "linear-gradient(135deg, #0369a1, #0891b2)" }}>
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            {content.title && <h2 className="font-display text-3xl font-semibold sm:text-4xl">{content.title}</h2>}
            {content.desc && <p className="mx-auto mt-4 max-w-3xl text-base leading-7 text-white/90 whitespace-pre-wrap">{content.desc}</p>}
            {content.subtitle && <p className="mx-auto mt-3 max-w-2xl text-sm text-white/70 whitespace-pre-wrap">{content.subtitle}</p>}
          </div>
        </section>
      );
    }

    // ── CTA ──
    if (section.type === "cta") {
      return (
        <section key={section.id} className="py-16 sm:py-20" style={{ backgroundColor: accent }}>
          <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
            {content.title && <h2 className="font-display text-3xl font-semibold text-white sm:text-4xl">{content.title}</h2>}
            {content.desc && <p className="mx-auto mt-3 max-w-2xl text-lg leading-8 text-white/80 whitespace-pre-wrap">{content.desc}</p>}
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              {checkoutEnabled && (
                <Button type="button" onClick={checkoutNow} disabled={!canCheckout} className="h-14 rounded-xl bg-white px-7 text-base font-semibold hover:bg-sky-50" style={{ color: accent }}>
                  <ShoppingBag className="mr-2 h-5 w-5" /> {content.cta_checkout || "Checkout Sekarang"}
                </Button>
              )}
              <WhatsappButton label={content.cta_whatsapp || "Konsultasi WhatsApp"} className="inline-flex h-14 items-center justify-center rounded-xl border border-white/30 px-7 text-base font-semibold text-white hover:bg-white/10" />
            </div>
          </div>
        </section>
      );
    }

    // ── SOLUTION ──
    if (section.type === "solution") {
      const image = getSectionImage(section);
      const points = content.points || [];
      return (
        <section key={section.id} className="py-16 sm:py-20" style={{ backgroundColor: bg }}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-2 items-center">
              {image && (
                <div className="relative">
                  <div className="aspect-square overflow-hidden rounded-3xl shadow-2xl">
                    <img src={image} alt={content.title || "Solution"} className="h-full w-full object-cover" />
                  </div>
                </div>
              )}
              <div>
                {content.title && <h2 className="font-display text-3xl font-semibold text-stone-950 sm:text-4xl">{content.title}</h2>}
                {content.desc && <TextBlock text={content.desc} className="mt-4 text-lg leading-7 text-stone-600" />}
                {points.length > 0 && (
                  <ul className="mt-6 space-y-3">
                    {points.map((p, i) => (
                      <li key={i} className="flex items-center gap-3 text-stone-800">
                        <span className="h-6 w-6 rounded-full bg-sky-100 text-sky-700 grid place-items-center text-sm font-bold">✓</span>
                        {p}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  {checkoutEnabled && (
                    <Button type="button" onClick={checkoutNow} disabled={!canCheckout} className="h-14 rounded-xl bg-sky-600 px-6 text-base font-semibold text-white shadow-lg shadow-sky-200 hover:bg-sky-700">
                      <ShoppingBag className="mr-2 h-5 w-5" /> Beli Sekarang
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    }

    // ── OFFER ──
    if (section.type === "offer") {
      const bonusItems = content.bonus_items || [];
      return (
        <section key={section.id} className="py-16 sm:py-20 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1e3a5f, #0e7490)" }}>
          <div className="absolute -top-20 -left-20 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 -right-20 h-80 w-80 rounded-full bg-sky-300/20 blur-3xl" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              {content.badge && <span className="inline-block rounded-full bg-white text-sky-700 px-4 py-1.5 text-xs font-bold uppercase tracking-widest">{content.badge}</span>}
              {content.title && <h2 className="mt-4 font-display text-3xl font-semibold sm:text-4xl">{content.title}</h2>}
            </div>
            <div className="mt-10 grid lg:grid-cols-2 gap-8 items-center">
              <div className="rounded-3xl bg-white text-stone-900 p-8 shadow-2xl">
                <div className="flex items-center gap-4">
                  {productImage && <img src={productImage} alt={displayName} className="h-24 w-24 rounded-2xl object-cover" />}
                  <div>
                    <p className="font-display text-xl font-bold">{displayName}</p>
                    <p className="text-sm text-stone-500">{product?.short_description || ""}</p>
                  </div>
                </div>
                <div className="mt-6 flex items-end gap-3">
                  {hasComparePrice && <span className="text-stone-400 line-through text-lg">{formatRupiah(comparePrice)}</span>}
                  <span className="font-display text-4xl font-bold text-sky-700">{price ? formatRupiah(price) : "Harga Promo"}</span>
                </div>
                {hasComparePrice && (
                  <span className="mt-1 inline-block bg-sky-100 text-sky-700 font-bold px-3 py-1 rounded-full text-sm">HEMAT {formatRupiah(comparePrice - price)}</span>
                )}
                {bonusItems.length > 0 && (
                  <ul className="mt-6 space-y-2 text-sm">
                    {bonusItems.map((item, i) => (
                      <li key={i} className="flex items-center gap-2"><span className="text-emerald-600">✓</span> {item}</li>
                    ))}
                  </ul>
                )}
                {content.coupon_code && (
                  <div className="mt-6 rounded-2xl border-2 border-dashed border-sky-300 bg-sky-50 p-4 text-center">
                    <div className="text-xs uppercase tracking-widest text-stone-500">🎁 Kode Kupon</div>
                    <div className="font-display font-extrabold text-2xl text-sky-700 mt-1">{content.coupon_code}</div>
                    {content.coupon_label && <div className="text-xs text-stone-600">{content.coupon_label}</div>}
                  </div>
                )}
                <div className="mt-6">
                  {checkoutEnabled && (
                    <Button type="button" onClick={checkoutNow} disabled={!canCheckout} className="h-14 w-full rounded-xl bg-sky-600 text-base font-semibold text-white hover:bg-sky-700">
                      🔥 Claim Diskon & Checkout
                    </Button>
                  )}
                </div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-sm uppercase tracking-widest text-white/80">Promo Berakhir Dalam</div>
                <div className="mt-3 flex gap-3 justify-center lg:justify-start">
                  {(() => {
                    const timeStr = timeLeft.split(":");
                    return [{ l: "Jam", v: timeStr[0] }, { l: "Menit", v: timeStr[1] }, { l: "Detik", v: timeStr[2] }].map((u) => (
                      <div key={u.l} className="rounded-2xl bg-white/15 backdrop-blur-md border border-white/30 p-4 min-w-[80px] text-center">
                        <div className="font-display text-4xl font-bold tabular-nums">{u.v}</div>
                        <div className="text-[10px] uppercase tracking-widest text-white/80">{u.l}</div>
                      </div>
                    ));
                  })()}
                </div>
                {content.desc && <p className="mt-6 text-white/90 max-w-md">{content.desc}</p>}
                <div className="mt-6">
                  <WhatsappButton label="Tanya Promo via WhatsApp" className="inline-flex h-14 items-center justify-center rounded-xl border border-white/30 px-6 text-base font-semibold text-white hover:bg-white/10" />
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    }

    // ── HARD SELL ──
    if (section.type === "hard_sell") {
      const image = getSectionImage(section);
      const points = content.points || [];
      return (
        <section key={section.id} className="py-16 sm:py-20" style={{ backgroundColor: bg }}>
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-center">
              {image && (
                <div className="aspect-square overflow-hidden rounded-3xl shadow-2xl">
                  <img src={image} alt={content.title || "Product"} className="h-full w-full object-cover" />
                </div>
              )}
              <div>
                {content.title && (
                  <blockquote className="font-display text-2xl sm:text-3xl font-bold leading-tight text-stone-950">
                    "{content.title}"
                  </blockquote>
                )}
                {content.desc && <TextBlock text={content.desc} className="mt-5 text-base leading-7 text-stone-600" />}
                {points.length > 0 && (
                  <ul className="mt-6 space-y-2">
                    {points.map((p, i) => (
                      <li key={i} className="flex items-center gap-2 text-stone-800">
                        <span>🌺</span> {p}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-8">
                  {checkoutEnabled && (
                    <Button type="button" onClick={checkoutNow} disabled={!canCheckout} className="h-14 rounded-xl bg-sky-600 px-7 text-base font-semibold text-white shadow-lg shadow-sky-200 hover:bg-sky-700">
                      {content.cta_text || "YA, Saya Mau Produk Ini"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    }

    // ── URGENCY ──
    if (section.type === "urgency") {
      const remaining = content.remaining || 27;
      const total = content.total || 100;
      const pct = (remaining / total) * 100;
      return (
        <section key={section.id} className="py-16 sm:py-20" style={{ backgroundColor: bg }}>
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-block rounded-full bg-rose-50 text-rose-600 px-4 py-1.5 text-xs font-bold uppercase tracking-widest">⚠ Stok Hampir Habis</span>
            {content.title && <h2 className="mt-4 font-display text-3xl font-semibold text-stone-950 sm:text-4xl">{content.title}</h2>}
            {content.desc && <p className="mt-4 text-base leading-7 text-stone-600">{content.desc}</p>}
            <div className="mt-8 rounded-3xl bg-white border border-stone-200 p-6 shadow-sm">
              <div className="flex justify-between text-sm font-semibold">
                <span className="text-sky-700">Tersisa {remaining} Paket</span>
                <span className="text-stone-500">dari {total}</span>
              </div>
              <div className="mt-3 h-4 rounded-full bg-stone-100 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-sky-500 to-cyan-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
              <p className="mt-2 text-xs text-stone-500">Diperbarui hari ini</p>
            </div>
            <div className="mt-8">
              {checkoutEnabled && (
                <Button type="button" onClick={checkoutNow} disabled={!canCheckout} className="h-14 rounded-xl bg-sky-600 px-7 text-base font-semibold text-white shadow-lg shadow-sky-200 hover:bg-sky-700">
                  <ShoppingBag className="mr-2 h-5 w-5" /> Checkout Sekarang
                </Button>
              )}
            </div>
          </div>
        </section>
      );
    }

    // ── GUARANTEE ──
    if (section.type === "guarantee") {
      const items = content.items || [];
      return (
        <section key={section.id} className="py-16 sm:py-20 bg-gradient-to-r from-sky-50 to-cyan-50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {content.title && <h2 className="text-center font-display text-3xl font-semibold text-stone-950 sm:text-4xl">{content.title}</h2>}
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {items.map((item, i) => (
                <div key={i} className="rounded-3xl bg-white border border-stone-100 p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                  <div className="text-4xl">{item.icon}</div>
                  <h3 className="mt-3 font-bold text-stone-950">{item.title}</h3>
                  <p className="text-xs text-stone-500 mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-12 text-center">
              {checkoutEnabled && (
                <Button type="button" onClick={checkoutNow} disabled={!canCheckout} className="h-14 rounded-xl bg-sky-600 px-7 text-base font-semibold text-white shadow-lg shadow-sky-200 hover:bg-sky-700">
                  🔥 Pesan Sekarang Juga
                </Button>
              )}
            </div>
          </div>
        </section>
      );
    }

    // ── FINAL CTA ──
    if (section.type === "final_cta") {
      return (
        <section key={section.id} className="relative overflow-hidden py-20 sm:py-28 text-white" style={{ background: "linear-gradient(135deg, #1e3a5f, #0e7490)" }}>
          <div className="absolute -top-20 -left-20 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 -right-20 h-80 w-80 rounded-full bg-sky-300/20 blur-3xl" />
          <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
            {content.badge && <span className="inline-block rounded-full bg-white/15 backdrop-blur-md border border-white/30 px-4 py-1.5 text-xs font-bold uppercase tracking-widest">{content.badge}</span>}
            {content.title && <h2 className="mt-5 font-display text-4xl sm:text-5xl font-bold leading-tight">{content.title}</h2>}
            {content.desc && <p className="mt-5 text-white/90 text-lg max-w-xl mx-auto">{content.desc}</p>}
            <div className="mt-10 inline-block rounded-3xl bg-white text-stone-900 p-8 shadow-2xl">
              <div className="text-xs uppercase tracking-widest text-stone-500">Harga Promo Hari Ini</div>
              <div className="font-display text-5xl sm:text-6xl font-bold text-sky-700 leading-none mt-1">
                {price ? formatRupiah(price) : "Harga Promo"}
              </div>
              {hasComparePrice && <div className="text-sm text-stone-400 mt-2 line-through">{formatRupiah(comparePrice)}</div>}
              {content.coupon_code && (
                <div className="mt-6 rounded-2xl border-2 border-dashed border-sky-300 bg-sky-50 px-5 py-3">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">{content.coupon_label || "Kode Diskon"}</div>
                  <div className="font-display font-extrabold text-2xl text-sky-700">{content.coupon_code}</div>
                </div>
              )}
              <div className="mt-6">
                {checkoutEnabled && (
                  <Button type="button" onClick={checkoutNow} disabled={!canCheckout} className="h-14 w-full rounded-xl bg-sky-600 text-base font-semibold text-white hover:bg-sky-700">
                    <ShoppingBag className="mr-2 h-5 w-5" /> {content.cta_checkout || "Checkout Sekarang"}
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-6">
              <WhatsappButton label={content.cta_whatsapp || "Konsultasi WhatsApp"} className="inline-flex h-14 items-center justify-center rounded-xl border border-white/30 px-7 text-base font-semibold text-white hover:bg-white/10" />
            </div>
            <p className="mt-8 text-xs text-white/70 uppercase tracking-widest">🔒 Pembayaran Aman • 🚚 Dikirim Seluruh Indonesia</p>
          </div>
        </section>
      );
    }

    // ── PROMO COUNTDOWN (keep for backward compat) ──
    if (section.type === "promo_countdown") {
      return (
        <section key={section.id} className="py-16 text-white sm:py-20" style={{ backgroundColor: accent }}>
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <Clock3 className="mx-auto h-10 w-10 text-white/70" />
            {content.title && <h2 className="mt-5 font-display text-3xl font-semibold sm:text-4xl">{content.title}</h2>}
            {content.desc && <p className="mt-3 text-lg text-white/80 whitespace-pre-wrap">{content.desc}</p>}
            <div className="mx-auto mt-8 inline-flex rounded-2xl border border-white/20 bg-white/15 px-7 py-5">
              <span className="font-mono text-4xl font-bold">{timeLeft}</span>
            </div>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              {checkoutEnabled && (
                <Button type="button" onClick={checkoutNow} disabled={!canCheckout} className="h-14 rounded-xl bg-white px-6 text-base font-semibold hover:bg-sky-50" style={{ color: accent }}>
                  Checkout Sekarang
                </Button>
              )}
              <WhatsappButton label="Tanya Admin" className="inline-flex h-14 items-center justify-center rounded-xl border border-white/30 px-6 text-base font-semibold text-white hover:bg-white/10" />
            </div>
          </div>
        </section>
      );
    }

    // ── CUSTOM / FALLBACK ──
    return (
      <section key={section.id} className="py-16 sm:py-20" style={{ backgroundColor: bg }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {content.title && <h2 className="font-display text-3xl font-semibold text-stone-950 sm:text-4xl">{content.title}</h2>}
          {content.desc && <p className="mt-4 text-base leading-7 text-stone-600 whitespace-pre-wrap">{content.desc}</p>}
          {section.image_url && (
            <div className="mt-6 aspect-video overflow-hidden rounded-2xl">
              <img src={section.image_url} alt={content.title || ""} className="h-full w-full object-cover" />
            </div>
          )}
        </div>
      </section>
    );
  };

  if (loading) {
    return <main className="min-h-screen bg-slate-50 px-4 py-24 text-center text-stone-500">Memuat landing page...</main>;
  }

  if (!landingPage) {
    return <main className="min-h-screen bg-slate-50 px-4 py-24 text-center text-stone-500">Landing page tidak ditemukan.</main>;
  }

  // ─── Template branching: use dedicated template component if available ─────
  const templateKey = landingPage?.template?.key;
  if (templateKey === "kupon-conversion") {
    return <KuponConversionTemplate landingPage={landingPage} product={product} />;
  }

  return (
    <main className="bg-slate-50 text-stone-950" data-testid="single-product-lp">
      {previewMedia ? <MediaPreviewModal media={previewMedia} onClose={() => setPreviewMedia(null)} /> : null}
      <SEOHead
        title={landingPage.seo_title || `${displayName} - Landing Page`}
        description={landingPage.seo_description || landingPage.description}
        path={pagePath}
        image={product?.image || productImage}
        schema={schema}
      />

      {/* Sticky mobile CTA */}
      {showSticky && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-sky-100 bg-white/95 p-3 shadow-2xl backdrop-blur md:hidden">
          <div className="mx-auto flex max-w-md items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-stone-950">{displayName}</p>
              <p className="text-xs text-stone-500">{price ? formatRupiah(total) : "Tanya harga"}</p>
            </div>
            {checkoutEnabled ? (
              <Button type="button" onClick={checkoutNow} className="h-11 rounded-xl bg-sky-600 px-4 text-white hover:bg-sky-700" disabled={!canCheckout}>Checkout</Button>
            ) : (
              <WhatsappButton label="WhatsApp" className="inline-flex h-11 items-center justify-center rounded-xl bg-emerald-600 px-4 text-sm font-semibold text-white" />
            )}
          </div>
        </div>
      )}

      {/* Render all sections */}
      {sections.map(renderSection)}
    </main>
  );
};

export default SingleProductLandingPage;
